import React, { FC } from 'react';
import { Avatar } from '@kontentino/ui';
import TextEditor from 'app/modules/textEditor';
import { TextEditorFeature } from 'app/modules/textEditor/constants/textEditorFeature';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { useMentionableUsers } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useMentionableUsers';
import { useUnsavedComment } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useUnsavedComment';
import CommentsTextEditorReworkBadge from 'app/modules/posts/modules/post/components/postCommunication/comments/commentsTextEditor/CommentsTextEditorReworkBadge';
import CommentsTextEditorSubmitButton from 'app/modules/posts/modules/post/components/postCommunication/comments/commentsTextEditor/CommentsTextEditorSubmitButton';
import clsx from 'clsx';
import { useUpsertComment } from './hooks/useUpsertComment';
import { useTranslation } from 'react-i18next';
import CommentsTextEditorChangeDecisionClientApprovalDropdown from 'app/modules/posts/modules/post/components/postCommunication/comments/commentsTextEditor/CommentsTextEditorChangeDecisionClientApprovalDropdown';
import CommentsTextEditorReplyHeader from './CommentsTextEditorReplyHeader';
import { getColorVariant } from 'app/modules/posts/modules/post/components/postCommunication/utils/getColorVariant';
import { isReworkCommentAction } from '../utils/commentActions';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';

type Props = {};

const CommentsTextEditor: FC<Props> = () => {
  const {
    user: currentUser,
    activeContent,
    postId,
    commentAction,
    setCommentValue,
    commentValue,
    comment,
    resetCommentAction,
    setEditorRef: setCommunicationEditorRef,
  } = usePostCommunicationContext();
  const mentionableUsers = useMentionableUsers({ type: activeContent, postId });
  const { editorRef, setEditorRef, onSubmit, isLoading } = useUpsertComment();
  const isEditingComment = commentAction === 'edit';
  const showReplyHeader = commentAction === 'reply' && comment;

  useUnsavedComment({
    type: activeContent,
    postId,
    editorRef,
    comment,
    commentAction,
    isLoading,
  });

  const { t } = useTranslation();
  const isRework = isReworkCommentAction(commentAction);
  const { accountPlanType } = useSubscriptionInfo();

  return (
    <div className="tw-flex tw-flex-col tw-gap-y-2">
      {showReplyHeader && (
        <CommentsTextEditorReplyHeader
          comment={comment}
          colorVariant={getColorVariant({
            role: currentUser.role,
            thread: comment.thread,
          })}
        />
      )}
      <div
        className="tw-flex tw-items-end tw-gap-2"
        data-name="comments_text-editor"
      >
        <div className="tw-flex tw-items-center">
          <Avatar
            src={currentUser.avatar?.src || undefined}
            name={currentUser.name}
            size={36}
          />
          {isRework && <CommentsTextEditorReworkBadge />}
          {currentUser.role === 'client' && !commentAction && (
            <CommentsTextEditorChangeDecisionClientApprovalDropdown
              className="tw-ml-2"
              buttonSize="small"
              buttonVariant="ghost"
            />
          )}
        </div>
        <TextEditor
          accountPlanType={accountPlanType}
          dataPrefix="comments"
          mentionableUsers={mentionableUsers.data ?? []}
          resolvableMentionUsers={mentionableUsers.resolvable ?? []}
          placeholder={
            isRework
              ? t('commentsTextEditor.reworkPlaceholder')
              : t('commentsTextEditor.placeholder')
          }
          features={[
            TextEditorFeature.EmojiPicker,
            TextEditorFeature.UserMention,
            TextEditorFeature.FileAttachments,
          ]}
          isEditing={isEditingComment}
          onSaveEdit={() => onSubmit()}
          onCancelEdit={() => resetCommentAction()}
          initialValue={commentValue}
          onInit={(editor) => {
            setEditorRef(editor);
            setCommunicationEditorRef(editor);
          }}
          disabled={isLoading}
          onDebouncedChange={setCommentValue}
          classNames={{
            root: clsx(
              'tw-relative tw-min-w-0 tw-flex-1 tw-rounded-[1.25rem] tw-bg-grayscale-5 tw-ring-1 tw-ring-grayscale-5',
              {
                'tw-pl-[4.5rem]': isRework,
              },
              {
                'tw-pr-[3.5rem]': !isEditingComment,
              },
            ),
            rootFocused: 'tw-ring-primary-100',
            dropOverlay: 'tw-rounded-[1.5rem]',
            editorWrapper: 'tw-flex',
            editorContent: 'tw-flex-1',
            optionsBar:
              'tw-flex-shrink-0 tw-items-end tw-p-0 tw-py-2 tw-pr-4 tw-absolute -tw-bottom-1 tw-right-0',
            editor:
              'tw-min-h-[2.25rem] tw-py-2 tw-pl-4 tw-text-md tw-text-grayscale-180',
            editSaveButton: 'tw-rounded-full !tw-bg-transparent',
            editCancelButton: 'tw-rounded-full !tw-bg-transparent',
          }}
        />
        <CommentsTextEditorSubmitButton
          value={commentValue}
          activeContent={activeContent}
          isLoading={isLoading}
          onSubmit={onSubmit}
          disabled={isEditingComment}
        />
      </div>
    </div>
  );
};

export default CommentsTextEditor;
